import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { ChevronsRightIcon, Minus, Plus, Download } from "lucide-react";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import { fetchData, formatDateDMY, formatDateTimeDMY } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import { toast } from "react-toastify";
import Loader from "../../../components/partials/Loader";
import "font-awesome/css/font-awesome.min.css";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Eye } from "lucide-react";
import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";
import { getGrantTypeConfig } from "../grantTypeConfig";
import GrantImportButtons from "../GrantImportButtons";
import { exportSmeFamilyGrants } from "../grantExcelExport";

const SmeGrantList = ({ grantType = "sme" }) => {
  const cfg = getGrantTypeConfig(grantType);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [grants, setGrants] = useState([]);
  const [filterKey, setFilterKey] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasFilters, setHasFilters] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [tehsils, setTehsils] = useState([]);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedBusinessSector, setSelectedBusinessSector] = useState("");
  const [selectedCycle, setSelectedCycle] = useState(null);

  const [tempDistrict, setTempDistrict] = useState(null);
  const [tempTehsils, setTempTehsils] = useState([]);
  const [tempTehsil, setTempTehsil] = useState(null);
  const [tempStatus, setTempStatus] = useState("");
  const [tempBusinessSector, setTempBusinessSector] = useState("");
  const [tempCycle, setTempCycle] = useState(null);

  const [cycles, setCycles] = useState([]);

  const [updatingQualification, setUpdatingQualification] = useState(new Set());
  const [isQualifyModalVisible, setQualifyModalVisible] = useState(false);
  const [isNotQualifyModalVisible, setNotQualifyModalVisible] = useState(false);
  const [selectedQualifyId, setSelectedQualifyId] = useState(null);
  const [selectedNotQualifyId, setSelectedNotQualifyId] = useState(null);

  const checkFiltersApplied = () => {
    const hasActiveFilters = !!(
      search ||
      selectedDistrict ||
      selectedTehsil ||
      selectedStatus ||
      selectedBusinessSector ||
      selectedCycle
    );
    setHasFilters(hasActiveFilters);
  };

  useEffect(() => {
    checkFiltersApplied();
  }, [
    search,
    selectedDistrict,
    selectedTehsil,
    selectedStatus,
    selectedBusinessSector,
    selectedCycle,
  ]);

  const fetchGrants = useCallback(async () => {
    try {
      setLoader(true);
      const response = await fetchData(
        cfg.apiBase,
        "GET",
        null,
        {},
        {
          search,
          page: pagination.currentPage,
          limit: pagination.pageSize,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          overall_status: selectedStatus || undefined,
          business_sector: selectedBusinessSector || undefined,
          cycle_id: selectedCycle || undefined,
          grant_type: cfg.grantTypeValue,
        },
        navigate,
        cfg.routeBase,
        dispatch
      );

      setLoader(false);
      setGrants(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      setLoader(false);
      toast.error(`Failed to fetch ${cfg.label}s.`);
    }
  }, [
    search,
    pagination.currentPage,
    pagination.pageSize,
    selectedDistrict,
    selectedTehsil,
    selectedStatus,
    selectedBusinessSector,
    selectedCycle,
    navigate,
    dispatch,
  ]);

  useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  const fetchDistricts = async () => {
    try {
      const response = await fetchData(
        "districts",
        "GET",
        null,
        {},
        { status: 1 },
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchTehsils = async (districtId) => {
    if (!districtId) return [];
    try {
      const response = await fetchData(
        "tehsils",
        "GET",
        null,
        {},
        { district_id: districtId, status: 1 },
        navigate,
        "/tehsils",
        dispatch
      );
      return response?.data || [];
    } catch (error) {
      console.error("Error fetching tehsils:", error);
      return [];
    }
  };
  useEffect(() => {
    const loadTehsils = async () => {
      if (selectedDistrict) {
        const tehsilsData = await fetchTehsils(selectedDistrict);
        setTehsils(tehsilsData);
      } else {
        setTehsils([]);
      }
    };
    loadTehsils();
  }, [selectedDistrict]);

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchCycles = async () => {
    try {
      const response = await fetchData(
        "cycle",
        "GET",
        null,
        {},
        { status: 1 },
        navigate,
        "/cycle",
        dispatch
      );
      setCycles(response?.data || []);
    } catch (error) {
      console.error("Error fetching cycles:", error);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);
  const handleApplyFilters = async () => {
    try {
      setLoader(true);
      setSelectedDistrict(tempDistrict);
      setSelectedTehsil(tempTehsil);
      setSelectedStatus(tempStatus);
      setSelectedBusinessSector(tempBusinessSector);
      setSelectedCycle(tempCycle);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      const response = await fetchData(
        cfg.apiBase,
        "GET",
        null,
        {},
        {
          search,
          page: 1,
          limit: pagination.pageSize,
          district_id: tempDistrict || undefined,
          tehsil_id: tempTehsil || undefined,
          overall_status: tempStatus || undefined,
          business_sector: tempBusinessSector || undefined,
          cycle_id: tempCycle || undefined,
          grant_type: cfg.grantTypeValue,
        },
        navigate,
        cfg.routeBase,
        dispatch
      );

      setLoader(false);
      setGrants(response?.data || []);
      setPagination(response?.pagination || {});
      setFilterKey((prev) => prev + 1);
    } catch (error) {
      setLoader(false);
      toast.error(`Failed to fetch ${cfg.label}s.`);
    }
  };
  const handleClearFilters = async () => {
    try {
      setLoader(true);
      setTempDistrict(null);
      setTempTehsil(null);
      setTempStatus("");
      setTempBusinessSector("");
      setTempCycle(null);
      setTempTehsils([]);

      setSelectedDistrict(null);
      setSelectedTehsil(null);
      setSelectedStatus("");
      setSelectedBusinessSector("");
      setSelectedCycle(null);

      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      const response = await fetchData(
        cfg.apiBase,
        "GET",
        null,
        {},
        {
          search,
          page: 1,
          limit: pagination.pageSize,
          grant_type: cfg.grantTypeValue,
        },
        navigate,
        cfg.routeBase,
        dispatch
      );

      setLoader(false);
      setGrants(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      setLoader(false);
      toast.error(`Failed to fetch ${cfg.label}s.`);
    }
    setFilterKey((prev) => prev + 1);
  };
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };
  const handleDistrictChange = async (selected) => {
    setTempDistrict(selected || null);
    setTempTehsil(null); 
    if (selected) {
      const tehsilsData = await fetchTehsils(selected);
      setTempTehsils(tehsilsData);
    } else {
      setTempTehsils([]);
    }
  };
  const handleTehsilChange = (selected) => {
    setTempTehsil(selected || null);
  };
  const handleStatusChange = (selected) => {
    setTempStatus(selected || "");
  };
  const handleBusinessSectorChange = (selected) => {
    setTempBusinessSector(selected || "");
  };
  const handleCycleChange = (selected) => {
    setTempCycle(selected || null);
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const fetchAllGrantsForExport = async () => {
    try {
      const response = await fetchData(
        cfg.apiBase,
        "GET",
        null,
        {},
        {
          search,
          page: 1,
          limit: 10000,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          overall_status: selectedStatus || undefined,
          business_sector: selectedBusinessSector || undefined,
          cycle_id: selectedCycle || undefined,
          grant_type: cfg.grantTypeValue,
        },
        navigate,
        cfg.routeBase,
        dispatch
      );

      return response?.data || [];
    } catch (error) {
      console.error("Error fetching all grants for export:", error);
      throw error;
    }
  };

  const getBase64ImageFromURL = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute("crossOrigin", "anonymous");
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (error) => reject(error);
      img.src = url;
    });
  };

  const handleViewPdf = async (grantId) => {
    try {
      const response = await fetchData(
        `${cfg.apiBase}/${grantId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        `/${cfg.routeBase}`,
        dispatch
      );

      const grant = response?.data;
      if (!grant) {
        toast.error("No details found for this grant");
        return;
      }

      const logoRight = await getBase64ImageFromURL(
        require("../../../assets/media/photos/piadar_login_logo.png")
      );
      const logoLeft = await getBase64ImageFromURL(
        require("../../../assets/media/photos/logo1.png")
      );

      const borderedLayout = {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => "#ccc",
        vLineColor: () => "#ccc",
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      };

      let fieldVerificationDistrict = "N/A";
      if (grant.field_verification_district) {
        fieldVerificationDistrict =
          grant.field_verification_district.district_name || "N/A";
      }

      const formatDate = formatDateDMY;
      const isSedf = grant.grant_type === "SEDF Enterprise";
      const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "N/A");
      const hasValue = (v) => v !== null && v !== undefined && v !== "";
      const millions = (v) =>
        hasValue(v)
          ? Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })
          : "N/A";
      const fullAmount = (v) =>
        hasValue(v) ? Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "N/A";
      const amountAsMillions = (v) => (hasValue(v) ? millions(Number(v) / 1000000) : "N/A");
      const otherText = (value, other) =>
        value === "Other/Specify" ? `Other - ${other || "N/A"}` : value || "N/A";
      const sdgText = Array.isArray(grant.sdg_interventions) && grant.sdg_interventions.length
        ? grant.sdg_interventions.join(", ")
        : "N/A";
      const milestoneLabel = (n) => (Number(n) === 0 ? "7% Advance" : `Milestone ${n}`);
      const milestones = [...(grant.milestones || [])].sort(
        (a, b) => Number(a.milestone_number) - Number(b.milestone_number)
      );
      const realMilestones = milestones.filter((m) => Number(m.milestone_number) !== 0);
      const difference = (amount, released) =>
        hasValue(released) ? millions(Math.max(0, Number(amount || 0) - Number(released))) : "N/A";
      const head = (text) => ({ text, bold: true, fillColor: "#f1f1f1" });

      const shareRows = [
        ["Project Cost", grant.project_cost_pkr, grant.project_cost_euro],
        ["UNIDO Grant Share", grant.unido_grant_share_pkr, grant.unido_grant_share_euro],
        ...(isSedf ? [["SEDF Share", grant.sedf_share_pkr, grant.sedf_share_euro]] : []),
        ["Applicant Share", grant.applicant_share_pkr, grant.applicant_share_euro],
      ].map(([label, pkr, euro]) => [
        label,
        fullAmount(pkr),
        amountAsMillions(pkr),
        fullAmount(euro),
        amountAsMillions(euro),
      ]);

      const milestonesBody = milestones.length
        ? [
            [
              head("Milestone"),
              head("Title / Documents Required"),
              head("Amount (M)\nPKR / Euro"),
              head("Released (M)\nPKR / Euro"),
              head("Difference (M)\nPKR / Euro"),
              head("Deadline"),
              head("Status"),
              head("Remarks"),
            ],
            ...milestones.map((m) => {
              const n = Number(m.milestone_number);
              const adjustedHere =
                grant.include_seven_percent_advance && n > 0 && n === Number(grant.seven_percent_adjust_milestone);
              return [
                { text: n === 0 ? "7% Advance" : `Milestone ${n}`, bold: true },
                {
                  stack: [
                    { text: n === 0 ? "-" : m.milestone_title || "N/A" },
                    ...(n !== 0 && m.documents_required ? [{ text: m.documents_required, color: "#555555" }] : []),
                    ...(adjustedHere ? [{ text: "7% advance adjusted here", fontSize: 7, color: "#b45309", italics: true }] : []),
                  ],
                },
                `${millions(m.amount_pkr)}\n${millions(m.amount_euro)}`,
                `${millions(m.amount_released_pkr)}\n${millions(m.amount_released_euro)}`,
                `${difference(m.amount_pkr, m.amount_released_pkr)}\n${difference(m.amount_euro, m.amount_released_euro)}`,
                n === 0 ? "-" : formatDate(m.deadline_date),
                m.status || "N/A",
                n === 0 ? "-" : m.remarks || "N/A",
              ];
            }),
          ]
        : [[{ text: "No milestones recorded", colSpan: 8, alignment: "center" }, "", "", "", "", "", "", ""]];

      const docDefinition = {
        content: [
          {
            columns: [
              {
                image: logoLeft,
                width: 150,
                alignment: "left",
                margin: [20, 15, 20, 0],
              },
              {
                text: `${cfg.shortLabel} Business Grant`,
                style: "mainHeader",
                alignment: "center",
                margin: [0, 15, 0, 0],
              },
              {
                image: logoRight,
                width: 60,
                alignment: "right",
                margin: [0, 15, 0, 0],
              },
            ],
            columnGap: 10,
            margin: [0, 0, 0, 20],
          },

          { text: "Administrative Information", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ["Cycle", grant.cycle?.cycle_name || "N/A", "District", grant.district?.district_name || "N/A"],
                ["Tehsil", grant.tehsil?.tehsil_name || "N/A", "UC", grant.uc_name || "N/A"],
                ["Village", grant.village_name || "N/A", "Latitude", grant.latitude || "N/A"],
                ["Longitude", grant.longitude || "N/A", "", ""],
              ],
            },
          },

          { text: "Beneficiary Details", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ["Application ID", grant.application_id || "N/A", "Company Name", grant.company_name || "N/A"],
                ["Applicant Name", grant.applicant_full_name || "N/A", "Father/Husband Name", grant.father_husband_name || "N/A"],
                ["CNIC Number", grant.cnic_number || "N/A", "Gender", grant.gender || "N/A"],
                ["Date of Birth", formatDate(grant.date_of_birth), "Person with Disability", yesNo(grant.person_with_disability)],
                ["Marital Status", grant.marital_status || "N/A", "Contact Number", grant.contact_number || "N/A"],
                ["Email", grant.email || "N/A", "Business Category", grant.business_category || "N/A"],
                [
                  "Business Sector",
                  otherText(grant.business_sector, grant.business_sector_other),
                  "Business Sub Type",
                  grant.business_sub_type || "N/A",
                ],
                ["SDG Interventions", { text: sdgText, colSpan: 3 }, "", ""],
                ["Address", { text: grant.address || "N/A", colSpan: 3 }, "", ""],
                ["Business Description", { text: grant.business_description || "N/A", colSpan: 3 }, "", ""],
                ["Date of Selection", formatDate(grant.date_of_selection), "", ""],
              ],
            },
          },

          { text: "Grant Details", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["24%", "19%", "19%", "19%", "19%"],
              body: [
                [head(""), head("PKR (Amount)"), head("PKR (Million)"), head("Euro (Amount)"), head("Euro (Million)")],
                ...shareRows,
              ],
            },
          },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                [
                  "Business Entity Type",
                  otherText(grant.business_entity, grant.business_entity_other),
                  "Grant Agreement #",
                  grant.grant_agreement_number || "N/A",
                ],
                [
                  "Date of Evaluation/Qualification Review",
                  formatDate(grant.evaluation_date),
                  "Combined Score (%)",
                  hasValue(grant.combined_score_percentage) ? grant.combined_score_percentage : "N/A",
                ],
                ["Effective Date (Signed)", formatDate(grant.effective_date), "Expiry Date", formatDate(grant.expiry_date)],
                [
                  "Approved Grant Amount (PKR) (Million)",
                  millions(grant.approved_grant_amount_pkr),
                  "Approved Grant Amount (EURO) (Million)",
                  millions(grant.approved_grant_amount_euro),
                ],
                [
                  "Minutes of Qualification Uploaded",
                  grant.qualification_minutes_uploaded ? "Yes" : "No",
                  "",
                  "",
                ],
              ],
            },
          },

          { text: "Milestones Progress", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                [
                  "Number of Milestones",
                  realMilestones.length || "N/A",
                  "Include 7% Advance",
                  grant.include_seven_percent_advance ? "Yes" : "No",
                ],
                [
                  "7% Advance Adjusted In",
                  grant.include_seven_percent_advance && grant.seven_percent_adjust_milestone
                    ? milestoneLabel(grant.seven_percent_adjust_milestone)
                    : "N/A",
                  "",
                  "",
                ],
              ],
            },
          },
          {
            style: "tableBlock",
            layout: borderedLayout,
            fontSize: 8,
            table: {
              headerRows: 1,
              widths: ["11%", "19%", "13%", "13%", "13%", "9%", "9%", "13%"],
              body: milestonesBody,
            },
          },

          { text: "Verification & Support", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ["Field Officer Name", grant.field_officer_name || "N/A", "Date of Field Verification", formatDate(grant.field_verification_date)],
                ["District of Field Verification", fieldVerificationDistrict, "Technical Assistance Provided", yesNo(grant.technical_assistance_provided)],
                ["Verification Approved", yesNo(grant.verification_approved), "", ""],
                ["Remarks by Field Officer", { text: grant.field_officer_remarks || "N/A", colSpan: 3 }, "", ""],
              ],
            },
          },

          { text: "Overall Progress", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ...(grant.initial_overall_status
                  ? [
                      ["Status of Overall Grant (First Entry)", grant.initial_overall_status, "Extended Expiry Date", formatDate(grant.initial_extended_expiry_date)],
                      ["Remarks (First Entry)", { text: grant.initial_overall_remarks || "N/A", colSpan: 3 }, "", ""],
                    ]
                  : []),
                grant.overall_status === "Delayed"
                  ? ["Status of Overall Grant", grant.overall_status, "Extended Expiry Date", formatDate(grant.extended_expiry_date)]
                  : ["Status of Overall Grant", grant.overall_status || "N/A", "Date of Completion", formatDate(grant.completion_date)],
                ["Remarks (Overall)", { text: grant.overall_remarks || "N/A", colSpan: 3 }, "", ""],
              ],
            },
          },
        ],
        pageBreakBefore: (node, followingNodesOnPage) =>
          node.headlineLevel === 1 && followingNodesOnPage.length === 0,
        styles: {
          mainHeader: {
            fontSize: 20,
            bold: true,
            color: "#137e81",
          },
          tabHeader: {
            fontSize: 14,
            bold: true,
            margin: [0, 10, 0, 6],
            color: "#137e81",
            decoration: "underline",
          },
          tableBlock: {
            margin: [0, 0, 0, 15],
          },
        },
        defaultStyle: {
          fontSize: 9,
        },
      };

      pdfMake.createPdf(docDefinition).open();
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const exportData = await fetchAllGrantsForExport();
      if (!exportData.length) {
        toast.warning("No data available to export");
        return;
      }
      exportSmeFamilyGrants(exportData, {
        label: cfg.label,
        fileBase: `${cfg.apiBase}${hasFilters ? "-filtered" : ""}`,
        isSedf: cfg.grantTypeValue === "SEDF Enterprise",
        districts,
      });
      toast.success(`${cfg.label} data exported successfully`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export data to Excel");
    } finally {
      setIsExporting(false);
    }
  };
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (search !== undefined) {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        fetchGrants();
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [search]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchGrants();
  }, [
    selectedDistrict,
    selectedTehsil,
    selectedStatus,
    selectedBusinessSector,
    selectedCycle,
  ]);

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handleDelete = async () => {
    setModalVisible(false);
    try {
      await fetchData(
        `${cfg.apiBase}/${selectedId}`,
        "DELETE",
        null,
        {},
        {},
        navigate,
        `/${cfg.routeBase}`,
        dispatch
      );
      toast.success(`${cfg.label} deleted successfully.`);
      fetchGrants();
    } catch (error) {
      toast.error(`Failed to delete ${cfg.label}.`);
    }
  };

  const showDeleteModal = (id) => {
    setSelectedId(id);
    setModalVisible(true);
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const formatCurrency = (amount) =>
    `Rs ${(Number(amount) || 0).toLocaleString("en-US", { maximumFractionDigits: 6 })} M`;

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-[#137e81]";
      case "In progress":
        return "bg-blue-500";
      case "Delayed":
        return "bg-yellow-500";
      case "Postponed":
        return "bg-red-500";
      case "Cancelled":
        return "bg-orange-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatQualificationDateTime = formatDateTimeDMY;

  const showQualifyModal = (grantId) => {
    setSelectedQualifyId(grantId);
    setQualifyModalVisible(true);
  };

  const showNotQualifyModal = (grantId) => {
    setSelectedNotQualifyId(grantId);
    setNotQualifyModalVisible(true);
  };

  const handleQualify = async () => {
    setQualifyModalVisible(false);
    if (updatingQualification.has(selectedQualifyId)) return;

    setUpdatingQualification((prev) => new Set([...prev, selectedQualifyId]));

    try {
      await fetchData(
        `${cfg.apiBase}/${selectedQualifyId}/qualification`,
        "PUT",
        { is_qualified: true },
        {},
        {},
        navigate,
        `/${cfg.routeBase}`,
        dispatch
      );

      toast.success("Grant qualified successfully");
      fetchGrants();
    } catch (error) {
      toast.error(`Failed to qualify grant: ${error.message}`);
    } finally {
      setUpdatingQualification((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedQualifyId);
        return newSet;
      });
    }
  };

  const handleNotQualify = async () => {
    setNotQualifyModalVisible(false);
    if (updatingQualification.has(selectedNotQualifyId)) return;

    setUpdatingQualification(
      (prev) => new Set([...prev, selectedNotQualifyId])
    );

    try {
      await fetchData(
        `${cfg.apiBase}/${selectedNotQualifyId}/qualification`,
        "PUT",
        { is_qualified: false },
        {},
        {},
        navigate,
        `/${cfg.routeBase}`,
        dispatch
      );

      toast.success("Grant marked as not qualified successfully");
      fetchGrants();
    } catch (error) {
      toast.error(`Failed to update qualification status: ${error.message}`);
    } finally {
      setUpdatingQualification((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedNotQualifyId);
        return newSet;
      });
    }
  };

  const columns = [
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Sr.No</span>
      ),
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Cycle</span>
      ),
      selector: (row) => row.cycle?.cycle_name || "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Application ID
        </span>
      ),
      selector: (row) => row.application_id || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Company Name
        </span>
      ),
      minWidth: "140px",
      selector: (row) => row.company_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Applicant Name
        </span>
      ),
      minWidth: "140px",
      selector: (row) => row.applicant_full_name || "N/A",
      sortable : true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">District</span>
      ),
      selector: (row) => row.district?.district_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Tehsil</span>
      ),
      selector: (row) => row.tehsil?.tehsil_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Grant Amount (M) (PKR)
        </span>
      ),
      selector: (row) => formatCurrency(row.approved_grant_amount_pkr || 0),
      sortable: true,
      width: "180px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Business Sector
        </span>
      ),
      selector: (row) =>
        row.business_sector
          ?.replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()) || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Sector Other Specify Name
        </span>
      ),
      selector: (row) =>
        row.business_sector === "Other/Specify"
          ? row.business_sector_other || "N/A"
          : "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Business Entity
        </span>
      ),
      selector: (row) => row.business_entity || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Entity Other Specify Name
        </span>
      ),
      selector: (row) =>
        row.business_entity === "Other/Specify"
          ? row.business_entity_other || "N/A"
          : "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Status</span>
      ),
      cell: (row) => {
        const status = row.overall_status;
        return (
          <span
            className={`py-1 px-2 text-[0.75rem] rounded-full font-semibold text-white ${getStatusColor(
              status
            )}`}
          >
            {status
              ?.replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Qualified
        </span>
      ),
      cell: (row) => {
        const isQualified = row.is_qualified === true;
        const isNotQualified = row.is_qualified === false;
        const qualificationDateTime = formatQualificationDateTime(
          row.qualification_datetime
        );

        return (
          <div className="flex flex-col items-center justify-start gap-1 py-2">
            {isQualified ? (
              <>
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-blue-100 text-blue-800 whitespace-nowrap">
                  Qualified
                </span>
                {qualificationDateTime && (
                  <div className="text-[0.50rem] text-gray-600 text-center whitespace-nowrap">
                    {qualificationDateTime}
                  </div>
                )}
              </>
            ) : isNotQualified ? (
              <>
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-red-100 text-red-800 whitespace-nowrap">
                  Not Qualified
                </span>
                {qualificationDateTime && (
                  <div className="text-[0.50rem] text-gray-600 text-center whitespace-nowrap">
                    {qualificationDateTime}
                  </div>
                )}
              </>
            ) : (
              <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-yellow-100 text-yellow-800">
                Pending
              </span>
            )}
          </div>
        );
      },
      width: "120px",
    },
  ];
  if (
    permissions.includes(`${cfg.permissionPrefix}-update`) ||
    permissions.includes(`${cfg.permissionPrefix}-approve`) ||
    permissions.includes(`${cfg.permissionPrefix}-reject`) ||
    permissions.includes(`${cfg.permissionPrefix}-delete`)
  ) {
    columns.push({
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Action</span>
      ),
      cell: (row) => (
        <div className="flex gap-1">
          <PermissionButton
            hasPermission={permissions.includes(`${cfg.permissionPrefix}-update`)}
            to={`/${cfg.routeBase}/edit/${row.grant_id}`}
            faIcon="fa-solid fa-pen-to-square fa-lg"
            onlyIcon
            enabledClass=""
            disabledClass="opacity-50 cursor-not-allowed"
          />

          {permissions.includes(`${cfg.permissionPrefix}-approve`) &&
            row.is_qualified !== true && (
              <button
                onClick={() => showQualifyModal(row.grant_id)}
                disabled={updatingQualification.has(row.grant_id)}
                title="Mark as Qualified"
                className="hover:scale-110 transition-transform"
              >
                <i className="fa-solid fa-check-circle fa-lg text-green-600 hover:text-green-800"></i>
              </button>
            )}

          {permissions.includes(`${cfg.permissionPrefix}-reject`) &&
            row.is_qualified !== false && (
              <button
                onClick={() => showNotQualifyModal(row.grant_id)}
                disabled={updatingQualification.has(row.grant_id)}
                title="Mark as Not Qualified"
                className="hover:scale-110 transition-transform"
              >
                <i className="fa-solid fa-times-circle fa-lg text-red-600 hover:text-red-800"></i>
              </button>
            )}

          <button
            onClick={() => handleViewPdf(row.grant_id)}
            title="View PDF"
            className="hover:scale-110 transition-transform"
          >
            <Eye className="text-green-600" size={18} />
          </button>

          <PermissionButton
            hasPermission={permissions.includes(`${cfg.permissionPrefix}-delete`)}
            faIcon="fa fa-trash fa-lg text-primaryDark"
            onlyIcon
            onClick={() => showDeleteModal(row.grant_id)}
            className="rounded-full bg-gray-200 text-[#137e81] hover:text-white"
            enabledClass=""
            disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
          />
        </div>
      ),
      width: "180px",
    });
  }

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  useEffect(() => {
    setTimeout(() => setLoader(false), 1000);
  }, []);

  const shouldShowExportButton = grants.length > 0;

  return (
    <div className="w-full px-4">
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            {cfg.label}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
          {(permissions.includes(`${cfg.permissionPrefix}-create`) ||
            permissions.includes(`${cfg.permissionPrefix}-import`)) && (
            <GrantImportButtons
              importType={grantType}
              label={cfg.label}
              onImported={fetchGrants}
              canDownloadSample={permissions.includes(`${cfg.permissionPrefix}-create`)}
              canImport={permissions.includes(`${cfg.permissionPrefix}-import`)}
            />
          )}
          <PermissionButton
            hasPermission={permissions.includes(`${cfg.permissionPrefix}-create`)}
            to="add"
            faIcon="fa fa-plus"
            text="Add"
            className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg/text-[15px] text-[12px]"
            enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
            disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
          />
          </div>
        </div>
      </div>

      <Accordion>
        <AccordionSummary
          expandIcon={
            isFiltersVisible ? (
              <Minus color="#ffff" size={14} />
            ) : (
              <Plus size={14} color="#ffff" />
            )
          }
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            backgroundColor: "#137e81",
            color: "#fff !important",
            "& .MuiAccordionSummary-content": {
              color: "#fff !important",
            },
          }}
          onClick={toggleFilters}
        >
          Filters
        </AccordionSummary>

        <AccordionDetails>
          <div className="flex flex-col gap-5 w-full">
            <div className="flex gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <ThinDropdownWithSearch required={false}
                  key={`cycle-${filterKey}`}
                  label="Cycle"
                  options={cycles?.map((cycle) => ({
                    value: cycle?.id,
                    label: cycle?.cycle_name,
                  }))}
                  value={tempCycle}
                  onChange={handleCycleChange}
                />
              </div>

              <div className="flex-1 min-w-[180px]">
                <ThinDropdownWithSearch required={false}
                  key={`district-${filterKey}`}
                  label="District"
                  options={districts?.map((dis) => ({
                    value: dis?.district_id,
                    label: dis?.district_name,
                  }))}
                  value={tempDistrict}
                  onChange={handleDistrictChange}
                />
              </div>

              <div className="flex-1 min-w-[180px]">
                <ThinDropdownWithSearch required={false}
                  key={`tehsil-${filterKey}`}
                  label="Tehsil"
                  options={tempTehsils?.map((t) => ({
                    value: t?.tehsil_id,
                    label: t?.tehsil_name,
                  }))}
                  value={tempTehsil}
                  onChange={handleTehsilChange}
                  disabled={!tempDistrict}
                />
              </div>

              <div className="flex-1 min-w-[180px]">
                <ThinDropdownWithSearch required={false}
                  key={`status-${filterKey}`}
                  label="Status"
                  options={[
                    { value: "Completed", label: "Completed" },
                    { value: "In progress", label: "In Progress" },
                    { value: "Delayed", label: "Delayed" },
                    { value: "Postponed", label: "Postponed" },
                  ]}
                  value={tempStatus}
                  onChange={handleStatusChange}
                />
              </div>

              <div className="flex-1 min-w-[180px]">
                <ThinDropdownWithSearch required={false}
                  key={`business-sector-${filterKey}`}
                  label="Business Sector"
                  options={[
                    { value: "Aquaculture & Fisheries", label: "Aquaculture & Fisheries" },
                    { value: "Agri-Food & Beverage Processing", label: "Agri-Food & Beverage Processing" },
                    { value: "Livestock, Poultry & Meat Value Chain", label: "Livestock, Poultry & Meat Value Chain" },
                    { value: "Textiles, Apparel & Handicrafts", label: "Textiles, Apparel & Handicrafts" },
                    { value: "Automotive & Light Engineering", label: "Automotive & Light Engineering" },
                    { value: "Construction Materials & Prefabrication", label: "Construction Materials & Prefabrication" },
                    { value: "Renewable Energy & Energy Services", label: "Renewable Energy & Energy Services" },
                    { value: "Health & Care Services", label: "Health & Care Services" },
                    { value: "Education, Training & EdTech", label: "Education, Training & EdTech" },
                    { value: "Tourism, Hospitality & Creative Services", label: "Tourism, Hospitality & Creative Services" },
                    { value: "ICT & Digital Services", label: "ICT & Digital Services" },
                    { value: "Waste, Recycling & Circular Economy", label: "Waste, Recycling & Circular Economy" },
                    { value: "Retail, Distribution & Service Trades", label: "Retail, Distribution & Service Trades" },
                    { value: "Other/Specify", label: "Other/Specify" },
                  ]}
                  value={tempBusinessSector}
                  onChange={handleBusinessSectorChange}
                />
              </div>
            </div>
            <div className="flex gap-2 items-end justify-end">
              <button
                onClick={handleClearFilters}
                className="bg-[#be9609] hover:bg-[#997800] text-white px-4 py-1.5 rounded-md font-semibold transition-colors duration-200 h-fit"
              >
                Clear
              </button>
              <button
                onClick={handleApplyFilters}
                className="bg-[#137e81] hover:bg-[#0f6b6e] text-white px-4 py-1.5 rounded-md font-semibold transition-colors duration-200 h-fit"
              >
                Apply
              </button>
            </div>
          </div>

        </AccordionDetails>
      </Accordion>

      <div className="rounded-lg my-[15px] shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            {cfg.label}s List
            {hasFilters && (
              <span className="ml-2 text-xs">(Filtered Results)</span>
            )}
          </h1>
          {shouldShowExportButton && (
            <button
              onClick={exportToExcel}
              disabled={isExporting}
              className="flex items-center gap-2 bg-[#137e81] hover:bg-[#0f6b6e] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={16} />
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
          )}
        </div>

        <div className="p-[1.25rem] w-full">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />
            <input
              onChange={handleSearchChange}
              value={search}
              placeholder="Search"
              type="search"
              className="font-[Light] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none min-w-[300px]"
            />
          </div>

          <DataTable
            columns={columns}
            persistTableHead
            data={grants}
            customStyles={customStyles}
            highlightOnHover
            progressPending={false}
          />
          <GeneralFooter
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            dataLength={grants.length}
          />
        </div>
      </div>

      <CustomConfirmModal
        isVisible={isQualifyModalVisible}
        onConfirm={handleQualify}
        onCancel={() => setQualifyModalVisible(false)}
        message={`Are you sure you want to qualify this ${cfg.shortLabel} grant?`}
      />

      <CustomConfirmModal
        isVisible={isNotQualifyModalVisible}
        onConfirm={handleNotQualify}
        onCancel={() => setNotQualifyModalVisible(false)}
        message={`Are you sure you want to mark this ${cfg.shortLabel} grant as not qualified?`}
      />

      <CustomConfirmModal
        isVisible={isModalVisible}
        onConfirm={handleDelete}
        onCancel={() => setModalVisible(false)}
        message={`Are you sure you want to delete this ${cfg.shortLabel} grant?`}
      />
    </div>
  );
};

export default SmeGrantList;